import invariant from 'invariant';
import { eventChannel } from 'redux-saga';
import { call, put, take } from 'typed-redux-saga/macro';

import { STATE_INITIAL, STATE_LOADING, STATE_SUCCESS } from 'Store/commonActions';
import { dynamicFeatureComplete } from 'Store/dynamicLoading/actions';

const PREFIX = 'ASYNC/';
const createActionType = (name: string, prefix: string = PREFIX): string => `${prefix}${name}`;

export const ASYNC_FEATURE_ACCOUNT = createActionType('ACCOUNT');
export const ASYNC_FEATURE_AUTH_ONBOARDING = createActionType('AUTH_ONBOARDING');
export const ASYNC_FEATURE_BOOST = createActionType('BOOST');
export const ASYNC_FEATURE_CONSENTS = createActionType('CONSENTS');
export const ASYNC_FEATURE_CONTACTS = createActionType('CONTACTS');
export const ASYNC_FEATURE_EMAIL_AND_SMS = createActionType('EMAIL_SMS');
export const ASYNC_FEATURE_EXPLORE = createActionType('EXPLORE');
export const ASYNC_FEATURE_FILTERS = createActionType('FILTERS');
export const ASYNC_FEATURE_INCENTIVES = createActionType('INCENTIVES');
export const ASYNC_FEATURE_INSENDIO = createActionType('INSENDIO');
export const ASYNC_FEATURE_INTRO_PRICE = createActionType('INTRO_PRICE');
export const ASYNC_FEATURE_LIKES_YOU = createActionType('LIKES_YOU');
export const ASYNC_FEATURE_MEDIA = createActionType('MEDIA');
export const ASYNC_FEATURE_MM = createActionType('ASYNC_FEATURE_MM');
export const ASYNC_FEATURE_MY_LIKES = createActionType('ASYNC_FEATURE_MY_LIKES');
export const ASYNC_FEATURE_NOTIFICATIONS = createActionType('NOTIFICATIONS');
export const ASYNC_FEATURE_MUTUAL_FRIENDS = createActionType('MUTUAL_FRIENDS');
export const ASYNC_FEATURE_PASSPORT = createActionType('PASSPORT');
export const ASYNC_FEATURE_PAYWALLS = createActionType('PAYWALLS');
export const ASYNC_FEATURE_PERFORMANCE_METRICS = createActionType('PERFORMANCE_METRICS');
export const ASYNC_FEATURE_PROFILE = createActionType('PROFILE');
export const ASYNC_FEATURE_PURCHASE = createActionType('PURCHASE');
export const ASYNC_FEATURE_RECS = createActionType('RECS');
export const ASYNC_FEATURE_REPORT = createActionType('REPORT');
export const ASYNC_FEATURE_SEND_MESSAGE_AND_MATCH = createActionType('SEND_MESSAGE_AND_MATCH');
export const ASYNC_FEATURE_SETTINGS = createActionType('SETTINGS');
export const ASYNC_FEATURE_SPOTIFY = createActionType('SPOTIFY');
export const ASYNC_FEATURE_STACKS_UTIL = createActionType('STACKS_UTIL');
export const ASYNC_FEATURE_TINDER_SELECT = createActionType('ASYNC_FEATURE_TINDER_SELECT');
export const ASYNC_FEATURE_SUB_OFFER = createActionType('SUB_OFFER');
export const ASYNC_FEATURE_UPDATES = createActionType('UPDATES');
export const ASYNC_FEATURE_USER_BIOMETRICS = createActionType('USER_BIOMETRICS');
export const ASYNC_FEATURE_USER_PHOTO = createActionType('USER_PHOTO');
export const ASYNC_FEATURE_USER_PROFILE = createActionType('USER_PROFILE');
export const ASYNC_FEATURE_VAULT = createActionType('VAULT');
export const ASYNC_FEATURE_WEBSOCKET = createActionType('WEBSOCKET');

const ASYNC_MAPPING = new Map();

function registerAsyncFeature(name: string, loadFeature: Function) {
  invariant(!ASYNC_MAPPING.has(name), `Cannot register ${name} twice!`);

  let bundleState = STATE_INITIAL;
  const channel = eventChannel((emit) => () => {
    bundleState = STATE_SUCCESS;
    emit(true);
  });

  const asyncFeatureBundle = {
    *load() {
      switch (bundleState) {
        case STATE_INITIAL:
          bundleState = STATE_LOADING;
          yield loadFeature();
          channel.close();
          yield* put(dynamicFeatureComplete(name));
          break;
        case STATE_LOADING:
          while (bundleState !== STATE_SUCCESS) {
            yield* take(channel);
          }
          break;
        default:
          break;
      }
      return bundleState;
    },
    *whenLoaded() {
      while (bundleState !== STATE_SUCCESS) {
        yield* take(channel);
      }
      return bundleState;
    },
  };

  ASYNC_MAPPING.set(name, asyncFeatureBundle);
}

async function loadAsyncFeatures() {
  const asyncFeatures = await import(
    './asyncSagaFeatures' /* webpackChunkName: "async-saga-features" */
  );
  const {
    loadAccountFeature,
    loadAuthOnboardingFeature,
    loadBoostFeature,
    loadConsentsFeature,
    loadContactsFeature,
    loadExploreFeature,
    loadFiltersFeature,
    loadIncentivesFeature,
    loadInsendioFeature,
    loadIntroPricingFeature,
    loadLikesYouFeature,
    loadMediaFeature,
    loadMMFeature,
    loadMutualFriendsFeature,
    loadMyLikesFeature,
    loadNotificationsFeature,
    loadPassportFeature,
    loadPaywallsFeature,
    loadPerformanceMetricsFeature,
    loadProfileFeature,
    loadPurchaseFeature,
    loadRecsFeature,
    loadReportFeature,
    loadSendMessageAndMatchFeature,
    loadSettingsFeature,
    loadSpotifyFeature,
    loadStacksUtilFeature,
    loadSubOfferFeature,
    loadUpdatesFeature,
    loadUserBiometricsFeature,
    loadUserPhotoFeature,
    loadUserProfileFeature,
    loadTinderSelectFeature,
    loadVaultFeature,
    loadWebsocketFeature,
  } = asyncFeatures;

  registerAsyncFeature(ASYNC_FEATURE_ACCOUNT, loadAccountFeature);
  registerAsyncFeature(ASYNC_FEATURE_AUTH_ONBOARDING, loadAuthOnboardingFeature);
  registerAsyncFeature(ASYNC_FEATURE_BOOST, loadBoostFeature);
  registerAsyncFeature(ASYNC_FEATURE_CONSENTS, loadConsentsFeature);
  registerAsyncFeature(ASYNC_FEATURE_CONTACTS, loadContactsFeature);
  registerAsyncFeature(ASYNC_FEATURE_EXPLORE, loadExploreFeature);
  registerAsyncFeature(ASYNC_FEATURE_FILTERS, loadFiltersFeature);
  registerAsyncFeature(ASYNC_FEATURE_INCENTIVES, loadIncentivesFeature);
  registerAsyncFeature(ASYNC_FEATURE_INSENDIO, loadInsendioFeature);
  registerAsyncFeature(ASYNC_FEATURE_INTRO_PRICE, loadIntroPricingFeature);
  registerAsyncFeature(ASYNC_FEATURE_LIKES_YOU, loadLikesYouFeature);
  registerAsyncFeature(ASYNC_FEATURE_MEDIA, loadMediaFeature);
  registerAsyncFeature(ASYNC_FEATURE_MM, loadMMFeature);
  registerAsyncFeature(ASYNC_FEATURE_MUTUAL_FRIENDS, loadMutualFriendsFeature);
  registerAsyncFeature(ASYNC_FEATURE_MY_LIKES, loadMyLikesFeature);
  registerAsyncFeature(ASYNC_FEATURE_NOTIFICATIONS, loadNotificationsFeature);
  registerAsyncFeature(ASYNC_FEATURE_PASSPORT, loadPassportFeature);
  registerAsyncFeature(ASYNC_FEATURE_PAYWALLS, loadPaywallsFeature);
  registerAsyncFeature(ASYNC_FEATURE_PERFORMANCE_METRICS, loadPerformanceMetricsFeature);
  registerAsyncFeature(ASYNC_FEATURE_PROFILE, loadProfileFeature);
  registerAsyncFeature(ASYNC_FEATURE_PURCHASE, loadPurchaseFeature);
  registerAsyncFeature(ASYNC_FEATURE_RECS, loadRecsFeature);
  registerAsyncFeature(ASYNC_FEATURE_REPORT, loadReportFeature);
  registerAsyncFeature(ASYNC_FEATURE_SEND_MESSAGE_AND_MATCH, loadSendMessageAndMatchFeature);
  registerAsyncFeature(ASYNC_FEATURE_SETTINGS, loadSettingsFeature);
  registerAsyncFeature(ASYNC_FEATURE_SPOTIFY, loadSpotifyFeature);
  registerAsyncFeature(ASYNC_FEATURE_STACKS_UTIL, loadStacksUtilFeature);
  registerAsyncFeature(ASYNC_FEATURE_SUB_OFFER, loadSubOfferFeature);
  registerAsyncFeature(ASYNC_FEATURE_UPDATES, loadUpdatesFeature);
  registerAsyncFeature(ASYNC_FEATURE_USER_BIOMETRICS, loadUserBiometricsFeature);
  registerAsyncFeature(ASYNC_FEATURE_USER_PHOTO, loadUserPhotoFeature);
  registerAsyncFeature(ASYNC_FEATURE_USER_PROFILE, loadUserProfileFeature);
  registerAsyncFeature(ASYNC_FEATURE_TINDER_SELECT, loadTinderSelectFeature);
  registerAsyncFeature(ASYNC_FEATURE_VAULT, loadVaultFeature);
  registerAsyncFeature(ASYNC_FEATURE_WEBSOCKET, loadWebsocketFeature);
}

let loadAsyncFeaturesPromise;
async function ensureLoadAsyncFeatures() {
  if (!loadAsyncFeaturesPromise) {
    loadAsyncFeaturesPromise = loadAsyncFeatures();
  }
  loadAsyncFeaturesPromise.catch(() => {
    loadAsyncFeaturesPromise = undefined;
  });
  return loadAsyncFeaturesPromise;
}

export function* loadAsyncBundle(type: string) {
  yield ensureLoadAsyncFeatures();
  const asyncFeature = ASYNC_MAPPING.get(type);
  if (asyncFeature) {
    yield* call([asyncFeature, 'load']);
  }
}

export function* awaitAsyncBundle(type: string) {
  yield ensureLoadAsyncFeatures();
  const asyncFeature = ASYNC_MAPPING.get(type);
  if (asyncFeature) {
    yield* call([asyncFeature, 'whenLoaded']);
  }
}
